import React, { useState, useEffect, useCallback, useContext } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { swalError } from "../../../utils/swalAlert";
import Table from "../../ui/Table";
import Navbar from "../../ui/Navbar";
import { AuthContext } from "../../../context/AuthContext";
import "../../../App.css";

export default function AdminWhatsAppNotifications() {
    const { isAuthenticated, primaryRole, user } = useContext(AuthContext);
    const instituteId = user?.instituteId;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date filter state
    const [showDatePopup, setShowDatePopup] = useState(false);
    const [rangeOption, setRangeOption] = useState("Today");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    // Notification type filter state
    const [selectedNotificationType, setSelectedNotificationType] = useState("");

    const breadcrumbs = [
        { name: "Home", path: "/admin" },
        {
            name: "Manage Institute",
            path: "/admin/manage-institute",
        },
        {
            name: "WhatsApp Notifications",
            path: "/admin/whatsapp-notifications",
            isLast: true,
        },
    ];

    /* ─── Date helpers ─────────────────────────────────────────────────────── */
    const userDate = useCallback((dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toISOString().slice(0, 10);
    }, []);

    const displayDateOnly = useCallback(
        (dateStr) => {
            if (!dateStr) return "";
            const date = new Date(dateStr);
            return date.toLocaleDateString(navigator.language, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },
        []
    );

    const getTimeRangeText = useCallback(() => {
        if (rangeOption === "Custom") {
            return startDate && endDate
                ? `${displayDateOnly(startDate)} – ${displayDateOnly(endDate)}`
                : "Select Date Range";
        }
        return rangeOption;
    }, [rangeOption, startDate, endDate, displayDateOnly]);

    const setTodayRange = useCallback(() => {
        const today = new Date();
        const startOfDay = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        setStartDate(userDate(startOfDay));
        setEndDate(userDate(today));
        setRangeOption("Today");
    }, [userDate]);

    const handlePresetSelect = (option) => {
        setRangeOption(option);
        const now = new Date();
        let start, end;

        const formatDate = (date) => {
            const offset = date.getTimezoneOffset();
            const localDate = new Date(date.getTime() - offset * 60000);
            return localDate.toISOString().slice(0, 10);
        };

        switch (option) {
            case "Today":
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                end = now;
                break;
            case "Last 24 Hours":
                start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                end = now;
                break;
            case "This Month":
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = now;
                break;
            case "Custom":
            default:
                return;
        }

        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
    };

    /* ─── Fetch ─────────────────────────────────────────────────────────────── */
    const fetchData = useCallback(async () => {
        if (!instituteId) return;
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            if (startDate) params.append("fromDate", startDate);
            if (endDate) {
                const endDateObj = new Date(endDate);
                endDateObj.setDate(endDateObj.getDate() + 1);
                params.append("toDate", endDateObj.toISOString().slice(0, 10));
            }
            params.append("instituteId", instituteId);

            const response = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/api/whatsapp-notification/get-all?${params.toString()}`,
                { withCredentials: true }
            );
            const notifications = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];
            setData(notifications);
        } catch (err) {
            setError(err.message);
            swalError(err.message);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, instituteId]);

    useEffect(() => {
        setTodayRange();
    }, [setTodayRange]);

    useEffect(() => {
        if (startDate && endDate && instituteId) {
            fetchData();
        }
    }, [fetchData, startDate, endDate, instituteId]);

    // Unique notification types from incoming data
    const notificationTypes = Array.from(
        new Set(data.map((d) => d.notificationType).filter(Boolean))
    ).sort();

    // Filtered data based on notification type
    const filteredData = selectedNotificationType
        ? data.filter((d) => d.notificationType === selectedNotificationType)
        : data;

    /* ─── Export to CSV ─────────────────────────────────────────────────────── */
    const exportToCSV = useCallback(() => {
        if (!filteredData.length) return;

        const headers = [
            "Notification Type",
            "Class",
            "Test Name",
            "Recipient Name",
            "WhatsApp Number",
            "Status",
            "Date & Time",
        ];

        const escapeCSV = (value) => {
            if (value == null) return "";
            const str = String(value);
            if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        // Force a value to be treated as a text string in Excel (prevents scientific notation)
        const forceText = (value) => {
            if (!value) return "—";
            return `="${String(value)}"`;
        };

        const rows = filteredData.map((row) => [
            row.notificationType || "—",
            row.className || "—",
            row.testName || "—",
            row.recipientName || "—",
            forceText(row.recipientPhone),   // force as text so Excel doesn't use scientific notation
            row.status || "—",
            row.createdAt
                ? new Date(row.createdAt).toLocaleString(navigator.language, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                  })
                : "—",
        ]);

        // Prepend UTF-8 BOM so Excel opens the file with correct encoding (fixes â€" → —)
        const BOM = "\uFEFF";
        const csvContent = BOM + [headers.map(escapeCSV).join(","), ...rows.map((r) => r.map(escapeCSV).join(","))].join("\r\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `whatsapp-notifications-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [filteredData]);

    /* ─── Stats ─────────────────────────────────────────────────────────────── */
    const stats = {
        total: filteredData.length,
        pending: filteredData.filter((d) => d.status === "pending").length,
        sent: filteredData.filter((d) => d.status === "sent").length,
        delivered: filteredData.filter((d) => d.status === "delivered").length,
        read: filteredData.filter((d) => d.status === "read").length,
        failed: filteredData.filter((d) => d.status === "failed").length,
    };

    /* ─── Columns (no Institute column since scoped to one institute) ─── */
    const columns = [
        {
            key: "notificationType",
            label: "Notification Type",
            sortable: true,
            render: (row) => row.notificationType || "—",
            filter: true
        },
        {
            key: "className",
            label: "Class",
            sortable: true,
            render: (row) => row.className || "—",
            filter: true
        },
        {
            key: "testName",
            label: "Test Name",
            sortable: true,
            render: (row) => row.testName || "—",
            filter: true
        },
        {
            key: "recipientName",
            label: "Recipient Name",
            sortable: true,
            render: (row) => row.recipientName || "—",
        },
        {
            key: "phoneNumber",
            label: "WhatsApp Number",
            sortable: true,
            render: (row) => row.recipientPhone || "—",
        },
        {
            key: "status",
            label: "Status",
            sortable: true,
            render: (row) => {
                const statusClass =
                    row.status === "delivered"
                        ? "status-badge badge-summarized"
                        : row.status === "failed"
                            ? "status-badge badge-failed"
                            : "status-badge badge-pending";
                return (
                    <span className={statusClass}>
                        {row.status
                            ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
                            : "—"}
                    </span>
                );
            },
            filter: true
        },
        {
            key: "createdAt",
            label: "Date & Time",
            sortable: true,
            render: (row) => {
                if (!row.createdAt) return "—";
                const d = new Date(row.createdAt);
                return d.toLocaleString(navigator.language, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            },
        },
    ];

    /* ─── Auth guard ─────────────────────────────────────────────────────────── */
    if (!isAuthenticated || primaryRole !== "admin") {
        return <Navigate to="/login" replace />;
    }

    /* ─── Render ─────────────────────────────────────────────────────────────── */
    return (
        <>
            <Navbar breadcrumbs={breadcrumbs} pageName="WhatsApp Notifications" />

            <div className="manage-token" style={{ maxWidth: "100%" }}>
                {/* ── Filters Row ──────────────────────────────────────────────── */}
                <div className="filters-row" style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
                    {/* Date Filter */}
                    <div className="time-range-wrapper">
                    <button
                        onClick={() => setShowDatePopup((prev) => !prev)}
                        className="time-range-btn"
                        aria-expanded={showDatePopup}
                        aria-haspopup="true"
                    >
                        <span className="btn-icon">🕒</span>
                        {getTimeRangeText()}
                        <span className={`chevron ${showDatePopup ? "open" : ""}`}>▼</span>
                    </button>

                    {showDatePopup && (
                        <>
                            <div
                                className="popup-overlay"
                                onClick={() => setShowDatePopup(false)}
                            />
                            <div className="time-range-popup">
                                <div className="popup-header">
                                    <h3>Select Time Range</h3>
                                    <button
                                        className="close-btn"
                                        onClick={() => setShowDatePopup(false)}
                                        aria-label="Close"
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="preset-section">
                                    <div className="preset-grid">
                                        {["Today", "Last 24 Hours", "This Month", "Custom"].map(
                                            (option) => (
                                                <button
                                                    key={option}
                                                    className={`preset-option ${rangeOption === option ? "selected" : ""
                                                        }`}
                                                    onClick={() => handlePresetSelect(option)}
                                                >
                                                    {option}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>

                                <div className="custom-range-section">
                                    <div className="date-inputs">
                                        <div className="input-group">
                                            <label htmlFor="admin-wa-start-date">Start Date</label>
                                            <input
                                                id="admin-wa-start-date"
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => {
                                                    setStartDate(e.target.value);
                                                    setRangeOption("Custom");
                                                }}
                                                className="date-input"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label htmlFor="admin-wa-end-date">End Date</label>
                                            <input
                                                id="admin-wa-end-date"
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => {
                                                    setEndDate(e.target.value);
                                                    setRangeOption("Custom");
                                                }}
                                                className="date-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                    {/* Notification Type Filter */}
                    <div className="time-range-wrapper" style={{ position: "relative" }}>
                        <div className="time-range-btn" style={{ pointerEvents: "none" }}>
                            <span className="btn-icon">🔔</span>
                            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {selectedNotificationType || "All Notification Types"}
                            </span>
                            <span className="chevron">▼</span>
                        </div>
                        <select
                            id="admin-notification-type-filter"
                            value={selectedNotificationType}
                            onChange={(e) => setSelectedNotificationType(e.target.value)}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                opacity: 0,
                                cursor: "pointer",
                            }}
                        >
                            <option value="">All Notification Types</option>
                            {notificationTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* ── Status Summary Cards ─────────────────────────────────────────── */}
                <motion.div
                    className="stats-grid"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    {/* Total */}
                    <div className="stat-card">
                        <div className="stat-icon total">💬</div>
                        <div className="stat-content">
                            <h3>Total</h3>
                            <p className="stat-number">{stats.total}</p>
                        </div>
                    </div>

                    {/* Pending */}
                    <div className="stat-card">
                        <div className="stat-icon input" style={{ background: "linear-gradient(135deg, #f59e0b22, #f59e0b44)" }}>⏳</div>
                        <div className="stat-content">
                            <h3>Pending</h3>
                            <p className="stat-number" style={{ color: "#f59e0b" }}>
                                {stats.pending}
                            </p>
                        </div>
                    </div>

                    {/* Sent */}
                    <div className="stat-card">
                        <div className="stat-icon input" style={{ background: "linear-gradient(135deg, #3b82f622, #3b82f644)" }}>📤</div>
                        <div className="stat-content">
                            <h3>Sent</h3>
                            <p className="stat-number" style={{ color: "#3b82f6" }}>
                                {stats.sent}
                            </p>
                        </div>
                    </div>

                    {/* Delivered */}
                    <div className="stat-card">
                        <div className="stat-icon output" style={{ background: "linear-gradient(135deg, #10b98122, #10b98144)" }}>✅</div>
                        <div className="stat-content">
                            <h3>Delivered</h3>
                            <p className="stat-number" style={{ color: "#10b981" }}>
                                {stats.delivered}
                            </p>
                        </div>
                    </div>

                    {/* Read */}
                    <div className="stat-card">
                        <div className="stat-icon output" style={{ background: "linear-gradient(135deg, #0ea5e922, #0ea5e944)" }}>👀</div>
                        <div className="stat-content">
                            <h3>Read</h3>
                            <p className="stat-number" style={{ color: "#0ea5e9" }}>
                                {stats.read}
                            </p>
                        </div>
                    </div>

                    {/* Failed */}
                    <div className="stat-card">
                        <div className="stat-icon cost" style={{ background: "linear-gradient(135deg, #ef444422, #ef444444)" }}>❌</div>
                        <div className="stat-content">
                            <h3>Failed</h3>
                            <p className="stat-number" style={{ color: "#ef4444" }}>
                                {stats.failed}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* ── Table ───────────────────────────────────────────────────────── */}
                <motion.div
                    className="table-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Loading WhatsApp notifications...</p>
                        </div>
                    ) : error ? (
                        <div className="error-container">
                            <div className="error-icon">⚠️</div>
                            <h2>Error Loading Data</h2>
                            <p>{error}</p>
                            <button onClick={fetchData} className="retry-btn">
                                <span>🔄</span> Retry
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="table-header">
                                <button
                                    className="export-csv-btn"
                                    onClick={exportToCSV}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    <span className="export-icon">📥</span>
                                    <span className="export-text">Export to CSV</span>
                                </button>
                            </div>
                            <Table
                                data={filteredData}
                                columns={columns}
                                caption={`WhatsApp Notifications (${filteredData.length})`}
                            />
                        </>
                    )}
                </motion.div>
            </div>
        </>
    );
}
